import React, { forwardRef, useContext, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Portal from '../../layout/Portal/Portal';
import TagWrapper from '../TagWrapper';
import useEventListener from '../../hooks/useEventListener';
import ThemeContext from '../../contexts/themeContext';
import useDeviceScreen from '../../hooks/useDeviceScreen';
import useDarkMode from '../../hooks/useDarkMode';
import './OffCanvas.css';

export const OffCanvasTitle = forwardRef(({ tag, id, children, className, ...props }, ref) => {
	return (
		<TagWrapper
			tag={tag}
			ref={ref}
			id={id}
			className={classNames('offcanvas-title', className)}
			{...props}>
			{children}
		</TagWrapper>
	);
});
OffCanvasTitle.propTypes = {
	id: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	tag: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span']),
};
OffCanvasTitle.defaultProps = {
	className: null,
	tag: 'h5',
};

export const OffCanvasHeader = forwardRef(
	({ children, className, onHandle, setOpen, ...props }, ref) => {
		return (
			<div ref={ref} className={classNames('offcanvas-header', className)} {...props}>
				{children}
				{setOpen && (
					<button
						type='button'
						className='btn-close text-reset'
						data-bs-dismiss='offcanvas'
						aria-label='Close'
						onClick={() => {
							setOpen(false);
							if (onHandle) {
								onHandle();
							}
						}}
					/>
				)}
			</div>
		);
	},
);
OffCanvasHeader.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	setOpen: PropTypes.func,
};
OffCanvasHeader.defaultProps = {
	className: null,
	setOpen: null,
};

export const OffCanvasBody = forwardRef(({ tag, children, className, ...props }, ref) => {
	return (
		<TagWrapper
			tag={tag}
			ref={ref}
			className={classNames('offcanvas-body', className)}
			{...props}>
			{children}
		</TagWrapper>
	);
});
OffCanvasBody.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	tag: PropTypes.oneOf(['div', 'span', 'section', 'form']),
};
OffCanvasBody.defaultProps = {
	className: null,
	tag: 'div',
};

const OffCanvas = ({
	id,
	titleId,
	children,
	placement,
	isBodyScroll,
	isBackdrop,
	isModalStyle,
	isOpen,
	setOpen,
	isRightPanel,
	tag: Tag,
	isModalOpen = false,
	...props
}) => {
	const initialProps = {
		isBackdrop: isRightPanel ? false : isBackdrop,
		isBodyScroll: isRightPanel ? true : isBodyScroll,
		placement: isRightPanel ? 'end' : placement,
	};

	const { setRightPanel } = useContext(ThemeContext);
	const deviceScreen = useDeviceScreen();
	const { themeStatus } = useDarkMode();

	useLayoutEffect(() => {
		setRightPanel(isRightPanel && deviceScreen?.width > 1200 && isOpen);
	});

	const ref = useRef(null);

	useLayoutEffect(() => {
		if (!initialProps.isBodyScroll && isOpen) {
			document.body.style.overflow = 'hidden';
			document.body.style.paddingRight = '0px';
		}
		return () => {
			document.body.style.overflow = '';
			document.body.style.removeProperty('padding-right');
		};
	});

	useLayoutEffect(() => {
		if (isOpen) {
			document.documentElement.classList.add('no-scroll');
		} else {
			document.documentElement.classList.remove('no-scroll');
		}
		return () => document.documentElement.classList.remove('no-scroll');
	}, [isOpen]);

	const startX = useRef(null);

	useLayoutEffect(() => {
		function handleTouchStart(e) {
			startX.current = e.touches[0].clientX;
		}

		function handleTouchEnd(e) {
			if (startX.current !== null) {
				const endX = e.changedTouches[0].clientX;
				const diffX = endX - startX.current;

				if (diffX > 100) {
					setOpen(false);
				}
				startX.current = null;
			}
		}

		const isMobileOrTab = deviceScreen?.width <= 1200;

		if (isOpen && isMobileOrTab && !isModalOpen) {
			document.addEventListener('touchstart', handleTouchStart);
			document.addEventListener('touchend', handleTouchEnd);
		}

		return () => {
			if (isMobileOrTab) {
				document.removeEventListener('touchstart', handleTouchStart);
				document.removeEventListener('touchend', handleTouchEnd);
			}
		};
	}, [isOpen, setOpen, deviceScreen, isModalOpen]);

	const closeCanvas = (event) => {
		if (ref.current && !ref.current.contains(event.target) && !isRightPanel && isBackdrop) {
			setOpen(false);
		}
	};

	const _placementAnimation = (initialProps.placement === 'start' && { x: '-100%' }) ||
		(initialProps.placement === 'top' && { y: '-100%' }) ||
		(initialProps.placement === 'bottom' && { y: '100%' }) || { x: '100%' };

	const MotionTagWrapper = motion[Tag];

	return (
		<Portal>
			<AnimatePresence exitBeforeEnter>
				{isOpen && (
					<>
						<MotionTagWrapper
							ref={ref}
							key='offCanvas'
							initial={{ opacity: 0, ..._placementAnimation }}
							animate={{ opacity: 1, x: '0%', y: '0%' }}
							exit={{ opacity: 0, ..._placementAnimation }}
							transition={{ ease: 'easeInOut', duration: 0.3 }}
							id={id}
							className={classNames(
								'offcanvas',
								`offcanvas-${initialProps.placement}`,
								{
									show: isOpen,
									'offcanvas-modal-style': isModalStyle,
									'offcanvas-right-panel':
										isRightPanel && deviceScreen?.width > 1200,
								},
							)}
							tabIndex='-1'
							aria-labelledby={titleId}
							data-bs-scroll={initialProps.isBodyScroll}
							data-bs-backdrop={initialProps.isBackdrop}
							style={{
								visibility: isOpen && 'visible',
								backgroundColor: themeStatus === 'light' ? '#ececec' : undefined,
							}}
							{...props}>
							{children}
						</MotionTagWrapper>
						{initialProps.isBackdrop && (
							<div className={classNames('offcanvas-backdrop', 'fade', 'show')} />
						)}
					</>
				)}
			</AnimatePresence>
		</Portal>
	);
};
OffCanvas.propTypes = {
	id: PropTypes.string,
	children: PropTypes.node.isRequired,
	placement: PropTypes.oneOf(['start', 'top', 'end', 'bottom']),
	titleId: PropTypes.string,
	isOpen: PropTypes.bool.isRequired,
	setOpen: PropTypes.func.isRequired,
	isBodyScroll: PropTypes.bool,
	isBackdrop: PropTypes.bool,
	isModalStyle: PropTypes.bool,
	isRightPanel: PropTypes.bool,
	tag: PropTypes.oneOf(['div', 'section', 'form']),
};
OffCanvas.defaultProps = {
	id: null,
	placement: 'end',
	titleId: null,
	isBodyScroll: false,
	isBackdrop: true,
	isModalStyle: false,
	isRightPanel: false,
	tag: 'div',
};

export default OffCanvas;
